import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import { TextField } from "@/components/base/FormControls";
import BrandLoader from "@/components/feature/BrandLoader";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";

export default function ResetPasswordPage() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <BrandLoader label="Verificando enlace..." />;

  if (!session) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-background-50 px-6 text-center">
        <BrandLogo size={88} />
        <div className="w-full max-w-sm rounded-lg border border-background-200 bg-background-100 p-6">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-lock-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-lg font-semibold text-foreground-950">Enlace no válido</h1>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            El enlace de recuperación expiró o ya fue usado. Solicita uno nuevo desde el inicio de
            sesión.
          </p>
          <Link
            to="/login"
            className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            Ir al inicio de sesión
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    await supabase.auth.signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background-50 px-5 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center">
          <BrandLogo size={78} className="mb-5" />
          <h1 className="font-heading text-xl font-bold text-foreground-950">Nueva contraseña</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Define una contraseña segura para tu cuenta.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
          <TextField
            label="Nueva contraseña"
            name="password"
            type="password"
            value={password}
            onChange={setPassword}
            required
            autoComplete="new-password"
          />
          <TextField
            label="Confirmar contraseña"
            name="confirm_password"
            type="password"
            value={confirm}
            onChange={setConfirm}
            required
            autoComplete="new-password"
          />

          {error ? (
            <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
              <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
                <i className="ri-error-warning-line text-base"></i>
              </span>
              <span className="flex-1 leading-snug">{error}</span>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? (
              <i className="ri-loader-4-line animate-spin text-base"></i>
            ) : (
              <i className="ri-lock-line text-base"></i>
            )}
            Guardar contraseña
          </button>
        </form>
      </div>
    </div>
  );
}